import axios from "axios";
import { useCallback, useEffect, useState, useRef } from "react";
import { Button, Col, Container, Form, Row, Spinner, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import { extractYouTubeVideoIds, toVideoUrl } from "../video-guide/video-guide-settings";
import Breadcrumbs from "../../components/breadcrumbs";

const SummaryGeneration = () => {
  const [sourceType, _setSourceType] = useState("url");
  const setSourceType = useCallback((val) => {
    setData("");
    _setSourceType(val);
    setUrlInputValue("");
    setSelectedSource({
      categoryId: 0,
      lesson: "",
      course: "",
      unit: "",
      url: "",
    });
  }, []);

  const [urlInputValue, setUrlInputValue] = useState("");
  const [selectedSource, setSelectedSource] = useState({
    categoryId: 0,
    lesson: "",
    course: "",
    unit: "",
    url: "",
  });
  const [courseArray, setCourseArray] = useState([]);
  const [catArray, setCatArray] = useState([]);
  const [unitArray, setUnitArray] = useState([]);
  const [lessonArray, setLessonArray] = useState([]);
  const [urlArray, setUrlArray] = useState([]);

  const [data, setData] = useState();
  const [isGeneratingTranscript, setIsGeneratingTranscript] = useState(false);
  const [transcriptStatus, setTranscriptStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const pollingRef = useRef(null);
  const validateUnits = useCallback((data) => {
    const temp = data.slice();
    const val = temp.filter((unit) => unit.name.trim() !== "" && unit.name != ".");
    return val;
  }, []);

  useEffect(() => {
    axios
      .post(
        "https://" +
          process.env.REACT_APP_LMS_URL +
          "/webservice/rest/server.php?wstoken=" +
          process.env.REACT_APP_LMS_TOKEN +
          "&wsfunction=local_course_service_get_category_list&moodlewsrestformat=json",
      )
      .then((response) => {
        if (response.data) {
          const { data } = response.data;
          setCatArray(data);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  useEffect(() => {
    let url =
      "https://" +
      process.env.REACT_APP_LMS_URL +
      "/webservice/rest/server.php?wstoken=" +
      process.env.REACT_APP_LMS_TOKEN +
      "&wsfunction=local_course_service_course_data&moodlewsrestformat=json&user_id=0&category_id=" +
      selectedSource.categoryId;

    axios
      .post(url)
      .then((response) => {
        setCourseArray(response.data.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [selectedSource.categoryId]);

  useEffect(() => {
    if (!selectedSource.course) return;
    axios
      .post(
        "https://" +
          process.env.REACT_APP_LMS_URL +
          "/webservice/rest/server.php?wstoken=" +
          process.env.REACT_APP_LMS_TOKEN +
          "&wsfunction=local_course_service_course_unit_data&moodlewsrestformat=json&course_id=" +
          selectedSource.course,
      )
      .then((response) => {
        if (response.data) {
          const decodedData = JSON.parse(response.data.data);
          let units = decodedData[0].units;
          setUnitArray(validateUnits(units));
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [selectedSource.course]);

  useEffect(() => {
    if (!selectedSource.unit) return;
    axios
      .post(
        "https://" +
          process.env.REACT_APP_LMS_URL +
          "/webservice/rest/server.php?wstoken=" +
          process.env.REACT_APP_LMS_TOKEN +
          "&wsfunction=local_course_service_unit_lessions_data&moodlewsrestformat=json&unit_id=" +
          selectedSource.unit,
      )
      .then((response) => {
        if (response.data) {
          setLessonArray(response.data.data);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [selectedSource.unit]);

  useEffect(() => {
    if (!selectedSource.lesson) return;
    const lesson = lessonArray.find((l) => l.Id.toString() === selectedSource.lesson);
    if (!lesson) return;
    const urls = extractYouTubeVideoIds(lesson.Lession_Content);
    setUrlArray(
      urls.map((url, i) => ({
        url,
        name: `https://www.youtube.com/embed/${url}`,
      })),
    );
  }, [selectedSource.lesson]);

  const startTranscriptGeneration = async (url) => {
    const endpoint = `${process.env.REACT_APP_API_URL}/video-finder-transcript-generation`;
    const response = await axios.post(
      endpoint,
      { url },
      { headers: { "Content-Type": "application/json" } },
    );
    return response.data;
  };

  const fetchTranscriptById = async (id) => {
    const endpoint = `${process.env.REACT_APP_API_URL}/video-finder-transcript-generation?id=${id}`;
    const response = await axios.get(endpoint);
    return response.data;
  };

  const pollTranscriptStatus = (id, videoUrl) => {
    pollingRef.current = setInterval(async () => {
      try {
        const result = await fetchTranscriptById(id);
        if (result?.data?.status === "COMPLETED") {
          clearInterval(pollingRef.current);
          if (result?.data?.transcript) {
            setTranscriptStatus("Transcript generated! Now generating summary...");
          }
          setIsGeneratingTranscript(false);

          setTimeout(() => {
            generateSummaryAfterTransript(videoUrl);
          }, 1000);
        } else if (!["INPROGRESS", "NOT_STARTED"].includes(result?.data?.status)) {
          clearInterval(pollingRef.current);
          setIsGeneratingTranscript(false);
          setTranscriptStatus("Transcript generation failed");
          toast.error("Transcript generation failed");
        }
      } catch (err) {
        clearInterval(pollingRef.current);
        setIsGeneratingTranscript(false);
        setTranscriptStatus("Error while generating transcript");
        toast.error("Error while fetching transcript");
      }
    }, 5 * 1000);
  };

  const generateSummaryAfterTransript = async (videoUrl) => {
    try {
      setLoading(true);
      const { data } = await axios.post(
        process.env.REACT_APP_API_URL + `/video-finder-transcript-summary`,
        { video_url: videoUrl },
      );

      if (data?.message && data.message.toLowerCase().includes("transcript not found")) {
        // Auto-generate transcript
        setIsGeneratingTranscript(true);
        setTranscriptStatus("Transcript not found! We are generating the transcript...");

        try {
          const { data } = await startTranscriptGeneration(videoUrl);
          if (!data) throw new Error("No data returned from transcript generation API");
          pollTranscriptStatus(data, videoUrl);
        } catch (transcriptError) {
          setIsGeneratingTranscript(false);
          setTranscriptStatus(
            transcriptError?.response?.data?.message || "Failed to start transcript generation",
          );
          toast.error("Failed to generate transcript");
          setLoading(false);
        }
      } else {
        setTranscriptStatus("");
        setData(data?.message);
        setLoading(false);
      }
    } catch (err) {
      toast.error("Failed to generate summary");
      console.log(err);
    }
  };

  const generateSummary = useCallback(async () => {
    const videoCode =
      sourceType === "course-content"
        ? selectedSource.url
        : extractYouTubeVideoIds(urlInputValue)[0];

    const videoUrl = `https://www.youtube.com/embed/${videoCode}`;

    setLoading(true);
    setIsGeneratingTranscript(false);
    setTranscriptStatus("");
    setData("");

    try {
      const { data } = await axios.post(
        process.env.REACT_APP_API_URL + `/video-finder-transcript-summary`,
        { video_url: videoUrl },
      );

      if (data?.message && data.message.toLowerCase().includes("transcript not found")) {
        // Auto-generate transcript
        setIsGeneratingTranscript(true);
        setTranscriptStatus("Transcript not found! We are generating the transcript...");

        try {
          const { data } = await startTranscriptGeneration(videoUrl);
          if (!data) throw new Error("No data returned from transcript generation API");
          pollTranscriptStatus(data, videoUrl);
        } catch (transcriptError) {
          setIsGeneratingTranscript(false);
          setTranscriptStatus(
            transcriptError?.response?.data?.message || "Failed to start transcript generation",
          );
          toast.error("Failed to generate transcript");
        }
      } else {
        setLoading(false);
        setData(data?.message);
      }
    } catch (err) {
      setLoading(false);
      toast.error("Failed to generate summary");
      console.log(err);
    }
  }, [sourceType, selectedSource, urlInputValue, isGeneratingTranscript]);

  // Cleanup polling on component unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  return (
    <>
      <Container className="my-3">
        <Breadcrumbs />
        <p className="section-title mb-5">Summary Generation</p>
        <div className="d-flex gap-4 mb-2">
          <Form.Check
            type="radio"
            id="radio-url"
            name="sourceType"
            checked={sourceType === "url"}
            onChange={() => setSourceType("url")}
            label="Direct URL"
            htmlFor="radio-url"
          />

          <Form.Check
            type="radio"
            id="radio-lesson"
            name="sourceType"
            checked={sourceType === "course-content"}
            onChange={() => setSourceType("course-content")}
            label="Lesson"
            htmlFor="radio-lesson"
          />
        </div>
        <Row>
          {sourceType === "url" && (
            <Col md="6" lg="8s">
              <Form.Group controlId="url-textbox" className="d-flex align-items-center pb-2">
                <Form.Control
                  type="text"
                  placeholder="Enter URL"
                  style={{ maxWidth: "100%" }}
                  value={urlInputValue}
                  onChange={(e) => setUrlInputValue(e.target.value)}
                />
              </Form.Group>
            </Col>
          )}
          {sourceType === "course-content" && (
            <>
              <Col md="6" lg="4">
                <Form.Group className="py-1" controlId="source">
                  <Form.Select
                    value={selectedSource.categoryId}
                    onChange={(e) =>
                      setSelectedSource((s) => ({
                        ...s,
                        categoryId: e.target.value,
                      }))
                    }
                  >
                    <option>Select Category</option>
                    {!!catArray &&
                      catArray.map((cat) => (
                        <option key={cat.Category_ID} value={cat.Category_ID}>
                          {cat.Category_Name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md="6" lg="4">
                <Form.Group className="py-1" controlId="source">
                  <Form.Select
                    value={selectedSource.course}
                    disabled={!selectedSource.categoryId}
                    onChange={(e) =>
                      setSelectedSource((s) => ({
                        ...s,
                        course: e.target.value,
                      }))
                    }
                  >
                    <option>Select Course</option>
                    {!!courseArray &&
                      courseArray.map((course) => (
                        <option key={course.Course_ID} value={course.Course_ID}>
                          {course.Course_Name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md="6" lg="4">
                <Form.Group className="py-1" controlId="source">
                  <Form.Select
                    value={selectedSource.unit}
                    onChange={(e) => setSelectedSource((s) => ({ ...s, unit: e.target.value }))}
                  >
                    <option>Select Unit</option>
                    {!!unitArray &&
                      unitArray.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md="6" lg="4">
                <Form.Group className="py-1" controlId="source">
                  <Form.Select
                    value={selectedSource.lesson}
                    onChange={(e) =>
                      setSelectedSource((s) => ({
                        ...s,
                        lesson: e.target.value,
                      }))
                    }
                  >
                    <option>Select Lesson</option>
                    {!!lessonArray &&
                      lessonArray.map((lesson) => (
                        <option key={lesson.Id} value={lesson.Id}>
                          {lesson.Lession_Name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md="6" lg="4">
                <Form.Group className="py-1" controlId="source">
                  <Form.Select
                    value={selectedSource.url}
                    onChange={(e) => setSelectedSource((s) => ({ ...s, url: e.target.value }))}
                  >
                    <option>Select Video</option>
                    {!!urlArray &&
                      urlArray.map((url) => (
                        <option key={url.url} value={url.url}>
                          {url.name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </>
          )}
          <Col md="6" lg="4" style={{ display: "flex", gap: "8px" }}>
            <Button
              variant="primary"
              disabled={
                loading || sourceType === "course-content" ? !selectedSource.url : !urlInputValue
              }
              onClick={() => generateSummary()}
              style={{ height: "48px" }}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Generating</span>
                </>
              ) : (
                "Generate"
              )}
            </Button>
          </Col>
        </Row>

        {/* Transcript Not Found Alert */}
        {(transcriptStatus || loading) && (
          <Row className="mt-4">
            <Col>
              <Alert variant="warning" className="d-flex align-items-center">
                {loading && (
                  <div style={{ width: "24px", textAlign: "center", marginRight: "10px" }}>
                    <img src="/images/loader.svg" style={{ width: "20px" }} />
                  </div>
                )}
                <p className="mb-0 text-black">
                  {transcriptStatus
                    ? transcriptStatus
                    : "We are generating summary, please wait..."}
                </p>
              </Alert>
            </Col>
          </Row>
        )}

        <div
          style={{
            textAlign: "left",
            marginTop: "20px",
            whiteSpace: "pre-wrap",
          }}
        >
          {data}
        </div>
      </Container>
    </>
  );
};

export default SummaryGeneration;
